// Traitements de fond (lecture et indexation des documents) : progression, erreurs lisibles, annulation, relance.
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, RotateCcw, Square } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { dateHeure, depuis } from '../../lib/format.ts';
import { Bouton, Chargement, EntetePage, EtatErreur, EtatVide, Panneau, Progression, useNotifier } from '../../components/ui.tsx';
import { BadgeStatutDocument, BadgeStatutTache } from '../../components/statuts.tsx';
import { NavDocumentation, libelleTache } from './commun.tsx';

interface Tache {
  id: string; kind: string; status: string; progress: number; progress_label: string | null; error: string | null; attempts: number;
  max_attempts: number; entity: string | null; entity_id: string | null; created_at: string; updated_at: string; finished_at: string | null;
}
interface ResumeVersion { id: string; version_label: string; status: string; document_id: string; title: string }

export function PageTraitements() {
  const { peut, moi } = useSession();
  const qc = useQueryClient();
  const notifier = useNotifier();
  const q = useQuery({
    queryKey: ['taches-documentation'], queryFn: () => api.get<Tache[]>('/api/taches'),
    refetchInterval: (r) => ((r.state.data ?? []).some((t) => ['en_file', 'en_cours', 'annulation_demandee'].includes(t.status)) ? 2000 : false),
  });
  const ids = [...new Set((q.data ?? []).filter((t) => t.entity === 'document_version' && t.entity_id).map((t) => t.entity_id!))];
  const resume = useQuery({
    queryKey: ['versions-resume', ids.join(',')], enabled: ids.length > 0,
    queryFn: () => api.get<ResumeVersion[]>(`/api/documents/versions/resume?ids=${ids.join(',')}`),
  });
  const parVersion = new Map((resume.data ?? []).map((r) => [r.id, r]));

  const roles = moi.roles.map((r) => r.code);
  // Le serveur n'autorise l'annulation / la relance qu'à l'auteur de la tâche ou à l'administrateur.
  // Un référent voit toutes les tâches sans savoir lesquelles sont les siennes : les actions ne lui sont pas proposées.
  const peutAgir = roles.includes('admin') || !roles.includes('referent');

  const agir = async (t: Tache, action: 'annuler' | 'relancer') => {
    try {
      await api.post(`/api/taches/${t.id}/${action}`);
      notifier(action === 'annuler' ? 'Annulation demandée.' : 'Traitement relancé.');
      qc.invalidateQueries({ queryKey: ['taches-documentation'] });
    } catch (e) { notifier(messageErreur(e), true); }
  };

  return (
    <div className="pile-l doc-module">
      <EntetePage titre="Traitements" sousTitre="Lecture, découpage et indexation des documents importés. Une erreur sur un fichier n’affecte pas les autres." />
      <NavDocumentation peutImporter={peut('doc.importer')} />
      {!peutAgir && <p className="petit secondaire">Seuls l’auteur d’un traitement et l’administrateur peuvent l’annuler ou le relancer.</p>}
      {q.isLoading && <Chargement lignes={4} />}
      {q.isError && <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />}
      {q.data && q.data.length === 0 && (
        <Panneau><EtatVide icone={<Activity />} titre="Aucun traitement" texte="Les traitements apparaissent ici après un import de document." /></Panneau>
      )}
      {q.data && q.data.length > 0 && (
        <Panneau serre titre="Traitements récents">
          <ul className="liste">
            {q.data.map((t) => {
              const v = t.entity_id ? parVersion.get(t.entity_id) : undefined;
              const enCours = ['en_file', 'en_cours'].includes(t.status);
              return (
                <li key={t.id} className="liste-element doc-tache">
                  <span className="liste-element-corps">
                    <span className="liste-element-titre">
                      {v ? <Link to={`/documentation/lecteur/${v.id}`}>{v.title} — version {v.version_label}</Link> : libelleTache(t.kind)}
                    </span>
                    <span className="tres-petit discret">
                      {libelleTache(t.kind)} · lancé {depuis(t.created_at)}
                      {t.finished_at ? ` · terminé le ${dateHeure(t.finished_at)}` : ''}
                      {t.attempts > 1 ? ` · tentative ${t.attempts} sur ${t.max_attempts}` : ''}
                    </span>
                    {enCours && <Progression pct={t.progress} libelle={t.progress_label ?? (t.status === 'en_file' ? 'En attente' : 'En cours')} />}
                    {t.error && <span className="champ-erreur">{t.error}</span>}
                    {t.entity === 'document_version' && !v && resume.data && <span className="tres-petit discret">Document non accessible ou supprimé.</span>}
                  </span>
                  <span className="doc-suivi-etat">
                    <BadgeStatutTache statut={t.status} />
                    {v && t.status === 'termine' && <BadgeStatutDocument statut={v.status} />}
                    {peutAgir && enCours && <Bouton compact variante="danger" icone={<Square aria-hidden />} onClick={() => agir(t, 'annuler')}>Annuler</Bouton>}
                    {peutAgir && ['echec', 'annule'].includes(t.status) && <Bouton compact icone={<RotateCcw aria-hidden />} onClick={() => agir(t, 'relancer')}>Relancer</Bouton>}
                  </span>
                </li>
              );
            })}
          </ul>
        </Panneau>
      )}
    </div>
  );
}
